import React, { useEffect, useRef } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import InfoMessageComponent from "@components-shared/InfoMessageComponent";
import {
	FormInputField,
	FormInputFieldCurrency,
} from "@components-shared/desktop/inputs";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { Button } from "@components/buttons";
import { QuickBetActionsTypeEnum } from "@data-types/analytics-tracker/QuickPlayInteractionTypeEnum";
import { TSection } from "@data-types/offer/live/Section";
import { useRootAppStore } from "@hooks";
import { BetOffer } from "@models";
import { TFormFilter } from "@state/quick-play-store/QuickBetStore";
import { THEME_SETTINGS } from "@theme-settings";
import { getCurrentCulture } from "@utils";
import { analyticsTracker } from "@utils/analytics";
import { observer } from "mobx-react";

import { EventOfferBettingType } from "../common/EventOfferBettingType";
import { QuickBetButtonType } from "./components/QuickBetButtonType";
import { QuickBetEventName } from "./components/QuickBetEventName";
import { QuickBetFavoritesCheckBox } from "./components/QuickBetFavoritesCheckBox";
import { QuickBetOddsGroup } from "./components/QuickBetOddsGroup";
import { QuickBetSportsMenuCheckBox } from "./components/QuickBetSportsMenuCheckBox";

export default observer(function QuickBetWidget() {
	const rootStore = useRootAppStore();

	const mappedBetSlip = rootStore.quickPlayStore.quickBetStore.betSlip;

	const { t } = useTranslation("mybets");

	if (!THEME_SETTINGS.features.isQuickPlayEnabled) {
		return null;
	}

	const methods = useForm<TFormFilter>({
		mode: "onChange",
		defaultValues: rootStore.quickPlayStore.quickBetStore
			.getOnloadFilter || {
			eventCount: rootStore.quickPlayStore.quickBetStore.EVENTS_COUNT,
			maxOddValue:
				rootStore.quickPlayStore.quickBetStore.MAX_COEFFICIENT_VALUE,
			minOddValue:
				rootStore.quickPlayStore.quickBetStore.MIN_COEFFICIENT_VALUE,
			stake:
				rootStore.userAuthStore.user?.stakeAmount?.toFixed(2) ||
				THEME_SETTINGS.defaultStake.toFixed(2),
			useUserFavorites: false,
			quickBetType: rootStore.quickPlayStore.quickBetStore.QUICK_TYPE,
			includeMenu: false,
		},
	});

	const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined
	);

	const currentValueRef = useRef<string>(methods.getValues("stake"));

	useEffect(() => {
		if (rootStore.quickPlayStore.quickBetStore.onLoadFilter) {
			methods.reset({
				...rootStore.quickPlayStore.quickBetStore.onLoadFilter,
				includeMenu: false,
			});
		}

		const fetchData = async () => {
			if (!rootStore.quickPlayStore.quickBetStore.betSlip) {
				rootStore.quickPlayStore.quickBetStore.fetchData(
					methods.getValues()
				);
			}
		};

		fetchData();

		return () => {
			rootStore.quickPlayStore.quickBetStore.saveFilter({
				...methods.getValues(),
				eventCount:
					methods.getValues("eventCount") > 0 &&
					methods.getValues("eventCount") < 11
						? methods.getValues("eventCount")
						: methods.formState.defaultValues?.eventCount ||
							rootStore.quickPlayStore.quickBetStore.EVENTS_COUNT,
			});

			rootStore.quickPlayStore.quickBetStore.setGeneratedSlipError(null);
		};
	}, []);

	const submitData = (e: TFormFilter) => {
		analyticsTracker.trackQuickPlayInteraction(
			QuickBetActionsTypeEnum.TicketGen
		);
		rootStore.quickPlayStore.quickBetStore.fetchData(e);
	};

	function onStakeChange(e: React.ChangeEvent<HTMLInputElement>) {
		{
			const value = parseFloat(e.target.value.replace(/,/g, "."));
			methods.setValue("stake", e.target.value.replace(/,/g, "."));

			if (!isNaN(value) && value > 0) {
				currentValueRef.current = e.target.value;

				methods.setValue("stake", e.target.value);

				if (value > 0) {
					currentValueRef.current = value.toFixed(2);

					clearTimeout(debounceRef.current);
				}
			}
		}
	}

	function onStakeBlur() {
		methods.setValue("stake", currentValueRef.current);
		if (debounceRef.current != null) {
			clearInterval(debounceRef.current);
			debounceRef.current = undefined;
		}
	}

	useEffect(() => {
		if (location.pathname.includes("live")) {
			if (location.pathname.includes(TSection.Matches)) {
				methods.setValue("includeMenu", false);
			}
			if (methods.getValues("quickBetType") !== "live") {
				methods.setValue("includeMenu", false);
			}
		} else if (
			!location.pathname.includes("live") &&
			methods.getValues("quickBetType") == "live"
		) {
			methods.setValue("includeMenu", false);
		}
	}, [methods.getValues("quickBetType")]);

	return (
		<>
			<ErrorBoundaryWithPropagation
				componentName={"Desktop/QuickBetWidget.tsx"}
			>
				<div className="widget widget--play card--shadowy">
					<div className="widget__header">
						<div className="widget__header__title">
							{t("QUICK_PLAY.TITLE")}
						</div>
					</div>
					{mappedBetSlip ? (
						mappedBetSlip.betSlipOffers?.length > 0 ? (
							<div className="slip__body">
								{mappedBetSlip?.betSlipOffers.map(
									(props: BetOffer, index) => {
										return (
											<div
												className="slip__event slip--quick__event"
												key={props.bettingOfferId}
											>
												<div className="slip__event__header">
													<QuickBetEventName
														offer={props}
													/>
												</div>
												<div className="slip__markets">
													<div className="slip__pick">
														<EventOfferBettingType
															offer={props}
															isWidget
															withCoefficient
														/>

														<Button
															type="button"
															className="slip__btn--outlined slip__pick__remove btn--square btn--square--xxsml btn--primary--inverted btn--animate-grow"
															iconOnly
															iconClassName="u-icon u-icon--tny u-icon--remove u-color--sent--danger"
															onClick={() =>
																rootStore.quickPlayStore.quickBetStore.removeFromBetSlip(
																	index
																)
															}
														/>
													</div>
												</div>
											</div>
										);
									}
								)}
							</div>
						) : null
					) : null}

					{rootStore.quickPlayStore.quickBetStore
						.generatedSlipErrorMessage ? (
						<div className="card__body">
							<InfoMessageComponent
								size="sml"
								type="danger"
								message={t(
									`${rootStore.quickPlayStore.quickBetStore.generatedSlipErrorMessage}`
								)}
								hasBorder={false}
								addClassName="message--centered"
								customIcon="u-icon--lrg u-icon--empty--slip u-color--sent--danger message__icon--danger"
							/>
						</div>
					) : null}

					<form
						onSubmit={methods.handleSubmit(submitData)}
						className="slip__details slip--quick__details"
					>
						<FormProvider {...methods}>
							<Button
								btnText={t(
									"QUICK_PLAY.QUICK_BET.CREATE_NEW_BET_SLIP"
								)}
								className="btn--wrap btn--wrap--med btn--block btn--primary--inverted btn--animate-rotate"
								iconClassName="u-icon--sml u-icon--refresh"
								iconLeft
								disabled={
									Object.keys(methods.formState.errors)
										?.length != 0 ||
									methods.formState.isSubmitting ||
									!methods.formState.isValid ||
									rootStore.quickPlayStore.loaderStore
										.isLoading ||
									rootStore.quickPlayStore.loaderStore
										.isLoadingProcess
								}
							/>

							<div
								className={`slip--quick__periods slip--quick__periods--${getCurrentCulture()}`}
							>
								{rootStore.quickPlayStore.quickBetStore.QUICK_BET_TYPES_ARRAY.map(
									(type) => (
										<QuickBetButtonType
											key={type.quickBetType}
											quickBetType={type.quickBetType}
											text={type.text}
											isWidget
										/>
									)
								)}
							</div>

							<div className="slip--quick__settings card--quick">
								<FormInputFieldCurrency
									label="SESSION.REALITY_CHECK.STAKE"
									name="stake"
									className="form__field card--quick__input u-mar--bottom--reset"
									labelClassName="input__label"
									validate={{
										isNotNegative: (value: number) =>
											value > 0 ||
											t(
												"QUICK_PLAY.QUICK_BET.GREATER_THAN_0",
												{ 0: 0 }
											),
									}}
									maxLength={10}
									required
									onChange={onStakeChange}
									onBlur={onStakeBlur}
								/>

								<div className="card--quick__body">
									<div className="card--quick__row">
										<QuickBetOddsGroup
											text="BET_SLIP.MIN_ODDS"
											type="minOddValue"
										/>
									</div>

									<div className="card--quick__row">
										<QuickBetOddsGroup
											text="BET_SLIP.MAX_ODDS"
											type="maxOddValue"
										/>
									</div>
									<div className="card--quick__row">
										<FormInputField
											label="QUICK_PLAY.QUICK_BET.NUMBER_OF_EVENTS"
											name="eventCount"
											className="form__field card--quick__input"
											labelClassName="input__label"
											validate={{
												isNotNegative: (
													value: number
												) =>
													value > 0 ||
													t(
														"QUICK_PLAY.QUICK_BET.GREATER_THAN_0",
														{ 0: 0 }
													),
												lessThanOrEqualTo10: (
													value: number
												) =>
													Number(value) < 11 ||
													t(
														"QUICK_PLAY.QUICK_BET.LESS_THAN_OR_EQUAL_TO_NUMBER",
														{ 0: 10 }
													),
											}}
											required
										/>
									</div>
									<QuickBetFavoritesCheckBox />
									<QuickBetSportsMenuCheckBox isWidget />
								</div>
							</div>
						</FormProvider>
					</form>
					<Button
						type="button"
						btnText={t("mybets:SHARE.ADD_TO_SLIP")}
						className="slip__button btn--base btn--block btn--accent"
						disabled={
							!rootStore.quickPlayStore.quickBetStore.betSlip
						}
						onClick={() => {
							if (
								!rootStore.quickPlayStore.quickBetStore.betSlip
							) {
								console.error("Bet Slip should not be null");
								return;
							}
							analyticsTracker.trackQuickPlayInteraction(
								QuickBetActionsTypeEnum.TicketUse
							);
							rootStore.betSlipStore.useBetSlip(
								{
									...rootStore.quickPlayStore.quickBetStore
										.betSlip,
								},
								parseFloat(methods.getValues("stake"))
							);
						}}
					/>
				</div>
			</ErrorBoundaryWithPropagation>
		</>
	);
});
